import { useEffect, useId, useState } from "react";

import { useKindOptions } from "@/hooks/useKindOptions";
import { useNewFieldName } from "@/hooks/useNewFieldName";
import type { AgentAnswers } from "@/lib/attributeReview/reviewRow.types";

import type { useRowDecision } from "../AttributeReviewRow/useRowDecision";
import { KindControl } from "../LeafPicker";

import { ExpanderSummary } from "./ExpanderSummary";
import { FieldFinder } from "./FieldFinder";
import { NewFieldName } from "./NewFieldName";
import { PickChoice } from "./PickChoice";

type Answers = { kind?: string | null }[];

type Choice = "existing" | "new";

type Props = {
  mapping: Record<string, unknown>;
  decision: ReturnType<typeof useRowDecision>;
  pickedPath: string | null;
  sessionId: string;
  store: string;
  marketplace: string;
  focusKey: number;
};

export function ChangePick({
  mapping,
  decision,
  pickedPath,
  sessionId,
  store,
  marketplace,
  focusKey,
}: Props) {
  const kinds = useKindOptions(sessionId);
  const groupName = useId();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [choice, setChoice] = useState<Choice | null>(null);
  useEffect(() => {
    if (focusKey > 0) {
      setDetailsOpen(true);
      setChoice("new");
    }
  }, [focusKey]);
  const newName = useNewFieldName(
    (mapping.ownPath as string | null) ?? null,
    pickedPath,
  );
  const kind = decision.kind ?? (mapping.kind as string | null) ?? null;
  const answers = (mapping.answers as Answers | null) ?? [];
  const splitKinds = [
    ...new Set(
      answers
        .map((answer) => answer.kind)
        .filter((value): value is string => typeof value === "string"),
    ),
  ].filter((value) => kinds.some((option) => option.kind === value));
  const aiPaths = (
    (mapping.agent as AgentAnswers | null)?.answers ?? []
  ).flatMap((answer) => (answer.path ? [answer.path] : []));
  const field = String(mapping.sourceAttribute ?? "this field");
  const setKind = (next: string) => decision.setKind({ kind: next });
  const use = (path: string, nextKind: string | null) => {
    decision.setPath(path, nextKind);
    setChoice(null);
  };

  return (
    <details
      open={detailsOpen}
      onToggle={(event) => setDetailsOpen(event.currentTarget.open)}
      className="group min-w-0"
    >
      <ExpanderSummary className="text-[13px] text-indigo-600 dark:text-indigo-300">
        Not this field? Change the pick
      </ExpanderSummary>
      <div className="flex flex-col gap-2.5 pt-3">
        <p className="text-[13px] text-slate-700 dark:text-slate-200">
          What should {field} fill instead?
        </p>
        <PickChoice
          name={groupName}
          title="Another SmartStore field"
          hint="Shared with other stores. The seller answers once."
          checked={choice === "existing"}
          onToggle={() =>
            setChoice((c) => (c === "existing" ? null : "existing"))
          }
        >
          <FieldFinder
            kinds={kinds}
            kind={kind}
            splitKinds={splitKinds.length > 1 ? splitKinds : []}
            fallbackFolder={(mapping.folder as string | null) ?? null}
            pickedPath={pickedPath}
            aiPaths={aiPaths}
            store={marketplace}
            onKind={setKind}
            onUse={use}
          />
        </PickChoice>
        <PickChoice
          name={groupName}
          title={`A new field, only for ${store}`}
          hint="No other store uses it yet. You can link others later."
          checked={choice === "new"}
          onToggle={() => setChoice((c) => (c === "new" ? null : "new"))}
        >
          {newName.prefix && (
            <NewFieldName
              key={focusKey}
              {...newName}
              autoFocus={focusKey > 0}
              pickedPath={pickedPath}
              store={store}
              kindControl={
                <KindControl
                  label="Kind of the new field"
                  kinds={kinds}
                  kind={kind}
                  splitKinds={splitKinds.length > 1 ? splitKinds : undefined}
                  onKind={setKind}
                />
              }
              onName={newName.setName}
              onUse={(path) => use(path, kind)}
            />
          )}
        </PickChoice>
      </div>
    </details>
  );
}
